import Select, { components, MultiValueGenericProps } from "react-select";
import { Tooltip } from "react-tooltip";
import { useEffect, useRef } from "react";

import "./DropdownSelect.scss";

const MultiValueContainer = (props: MultiValueGenericProps<any>) => {
  return (
    <div
      data-tooltip-id="selector-label-id"
      data-tooltip-content={props.data.label}
    >
      <components.MultiValueContainer {...props} />
    </div>
  );
};

interface DropdownSelectProps {
  width: number;
  title: string;
  all: string;
  data: any[];
  selected?: any[];
  preselected?: any;
  isPageSelector?: boolean;
  isTippingUser?: boolean;
  darkMode?: boolean;
  onChange: (val: any) => void;
  resetting?: boolean;
  onResetEnded?: () => void;
  isOpen?: boolean;
  onToggle?: () => void;
  id?: string;
  isDisabled?: boolean;
}

const DropdownSelect = (props: DropdownSelectProps) => {
  const scaleMultiplier =
    props.width > 1023
      ? props.width > 1279
        ? props.width > 1439
          ? props.width > 1919
            ? 1
            : 0.813
          : 0.781
        : 0.567
      : 0.567;
  const isMobile = props.width <= 1023;
  const selectRef = useRef<any>();

  useEffect(() => {
    if (props && props.resetting && selectRef.current) {
      if (selectRef.current) selectRef.current.clearValue();
      if (props.onResetEnded) props.onResetEnded();
    }
  }, [props]);

  return (
    <div className="custom-select">
      <Select
        className={
          props.isTippingUser
            ? "custom-select-inner tipping"
            : "custom-select-inner"
        }
        ref={selectRef}
        isDisabled={props.isDisabled}
        options={props.data}
        menuIsOpen={props.isOpen}
        onMenuOpen={() => {
          if (!props.isOpen && props.onToggle) {
            props.onToggle();
          }
        }}
        onMenuClose={() => {
          if (props.isOpen && props.onToggle) {
            props.onToggle();
          }
        }}
        styles={{
          control: (baseStyles, _) => ({
            ...baseStyles,
            width: isMobile
              ? "100%"
              : props.isPageSelector
              ? 8 * scaleMultiplier + "rem"
              : 14.5 * scaleMultiplier + "rem",
            height: 2.5 * scaleMultiplier + "rem",
            minHeight: 0,
            border: props.isPageSelector
              ? props.darkMode
                ? "1px solid var(--dark-blue)"
                : "1px solid var(--text-light-color)"
              : 0,
            borderRadius: 1.25 * scaleMultiplier + "rem",
            flexWrap: "nowrap",
            cursor: "pointer",
            background: !props.darkMode
              ? "var(--light-gray)"
              : props.isPageSelector
              ? "linear-gradient(to bottom, var(--light-nightblue), var(--dark-nightblue))"
              : "linear-gradient(to right, var(--dark-nightblue), var(--light-nightblue))",
          }),
          indicatorSeparator: (baseStyles, _) => ({
            ...baseStyles,
            display: "none",
          }),
          valueContainer: (baseStyles, _) => ({
            ...baseStyles,
            flexWrap: "nowrap",
            overflowX: "scroll",
            scrollbarWidth: "none",
          }),
          placeholder: (baseStyles, _) => ({
            ...baseStyles,
            color: props.darkMode
              ? props.preselected
                ? "var(--text-dark-color)"
                : "rgba(255, 255, 255, .75)"
              : "var(--text-light-hc-color)",
            fontSize: props.isPageSelector
              ? "var(--paragraph-font)"
              : "var(--medium-font)",
          }),
          singleValue: (baseStyles, _) => ({
            ...baseStyles,
            color: props.darkMode
              ? "var(--text-dark-color)"
              : "var(--text-light-hc-color)",
            fontSize: props.isPageSelector
              ? "var(--paragraph-font)"
              : "var(--medium-font)",
          }),
          dropdownIndicator: (baseStyles, _) => ({
            ...baseStyles,
            color: props.darkMode
              ? "var(--text-dark-color) !important"
              : "var(--text-light-hc-color) !important",
          }),
          menu: (baseStyles, _) => ({
            ...baseStyles,
            width: isMobile
              ? "100%"
              : props.isPageSelector
              ? 6 * scaleMultiplier + "rem"
              : 15 * scaleMultiplier + "rem",
            background: props.darkMode
              ? "linear-gradient(to right, var(--dark-nightblue), var(--light-nightblue))"
              : "var(--light-gray)",
            color: "var(--text-dark-color)",
            borderRadius: "5px",
            padding: 5 * scaleMultiplier + "px 0",
          }),
          option: (baseStyles, state) => ({
            ...baseStyles,
            backgroundColor: state.isFocused
              ? props.darkMode
                ? "var(--light-blue)"
                : "var(--dark-blue)"
              : "transparent",
            color: state.isFocused
              ? props.darkMode
                ? "var(--text-light-hc-color)"
                : "var(--text-dark-color)"
              : props.darkMode
              ? "var(--text-dark-color)"
              : "var(--text-light-hc-color)",
            cursor: "pointer",
            fontSize: props.isPageSelector
              ? "var(--paragraph-font)"
              : "var(--medium-font)",
          }),
        }}
        placeholder={
          <div
            className={
              "placeholder" + (props.preselected ? " preselected" : "")
            }
          >
            {props.preselected ? props.preselected.label : props.all}
          </div>
        }
        components={{ MultiValueContainer }}
        hideSelectedOptions={false}
        defaultValue={props.preselected}
        onChange={(val) => props.onChange(val)}
      />
      <Tooltip id="selector-label-id" />
    </div>
  );
};

export default DropdownSelect;
